import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MODELOS_OFERECIDOS, ROTAS } from '@remoto/shared';
import type { SdkCapacidades } from '@remoto/protocolo';
import { chamar } from '../api';
import { socketDoPainel } from '../socket/painel';

/**
 * A lista estática, para quando a máquina não responde.
 *
 * É a política da casa (`MODELOS_OFERECIDOS`), com os cinco esforços: se a máquina está
 * calada, oferecer demais não machuca — o CLI rebaixa sozinho e a verdade
 * volta pelo próximo `init`. Comandos não têm fallback: sem a máquina não há
 * como saber o que aquele disco oferece, e inventar lista seria botão morto.
 */
export function capacidadesEstaticas(): Pick<SdkCapacidades, 'comandos' | 'modelos'> {
  return {
    comandos: [],
    modelos: MODELOS_OFERECIDOS.map((m): SdkCapacidades['modelos'][number] => ({
      id: m.id,
      rotulo: m.rotulo,
      descricao: '',
      aceitaEsforco: true,
      esforcos: ['low', 'medium', 'high', 'xhigh', 'max'],
      /*
        Neutros de propósito: os três campos descrevem o que a MÁQUINA relata, e
        esta lista é justamente o caso em que ela está calada. Marcar um padrão
        aqui seria o painel decidindo por ela, e a escolha sobreviveria ao
        `init` seguinte na cabeça de quem olhou. `esforcosDoMotor` fica vazio
        porque estes cinco níveis são os do enum, que já estão logo acima.
      */
      esforcosDoMotor: [],
      padrao: false,
      esforcoPadrao: null,
    })),
  };
}

/**
 * As capacidades vivas de uma sessão: carga fria pelo REST, atualização pelo
 * socket (o daemon empurra quando a lista muda no meio da conversa — skill
 * descoberta, modelo trocado, modo saído do plano).
 *
 * Devolve sempre algo utilizável: quando a máquina está desligada ou a sessão
 * é observada, `degradado` liga e as listas estáticas entram no lugar.
 */
export function usarCapacidades(
  sessaoId: string,
  ativo = true,
): { capacidades: SdkCapacidades | null; degradado: boolean } {
  const cliente = useQueryClient();

  const consulta = useQuery({
    queryKey: ['capacidades', sessaoId],
    queryFn: () => chamar<SdkCapacidades>(`${ROTAS.sessoes}/${sessaoId}/capacidades`),
    enabled: ativo && sessaoId.length > 0,
    staleTime: 60_000,
    // 409 aqui é estado, não acidente: máquina desligada ou sessão dormindo.
    retry: false,
  });

  useEffect(() => {
    if (!ativo || !sessaoId) return;
    const socket = socketDoPainel();
    const aoChegar = (dados: SdkCapacidades): void => {
      if (dados.sessaoId !== sessaoId) return;
      cliente.setQueryData(['capacidades', sessaoId], dados);
    };
    // A sala `sessao:<id>` já foi assinada por quem mostra a conversa; aqui é
    // só ouvir o que chega nela.
    socket.on('sessao.capacidades', aoChegar);
    return () => {
      socket.off('sessao.capacidades', aoChegar);
    };
  }, [sessaoId, ativo, cliente]);

  return { capacidades: consulta.data ?? null, degradado: !consulta.data };
}
